<!--

  Usage:

  Data is of format: { value : count }.

  <BarChart :data="{ 0: 50, 1: 20, 2: 5, 3: 40, ... }">
    <template slot="title">Bar Chart Title</template>
  </BarChart>

  <BarChart :data="{ 0: { series1: 50, series2: 10 }, 1: { series1: 30, series2: 40 }, ... }">
    <template slot="title">Bar Chart Title</template>
  </BarChart>

-->

<template>
  <div class="BarChart" ref="container">
    <div class="text-large text-center">
      <slot name="title"></slot>
    </div>

    <div>
      <svg ref="chart"></svg>
    </div>

    <div ref="tooltip" class="Tooltip"></div>
  </div>
</template>

<script>
  import * as d3 from 'd3';
  import { colour, arrayGroup, arrayColumn, maxValue } from '@/legacy/lib//util';
  import { roundUp, round } from '@/legacy/lib//math';
  import { capitalize } from '@/legacy/lib//template';

  export default {
    props: {
      series: {
        type: Object
      },

      sort: {
        type: [ Boolean, String ]
      },

      groups: {
        type: Number,
        default: 10
      },

      data: {
        type: Object
      },

      stacked: {
        type: Boolean,
        default: false
      },

      barHeight: {
        type: Number,
        default: 20
      },

      margin: {
        type: Object,
        default: function () {
          return {
            top:    20,
            right:  60,
            bottom: 30,
            left:   140
          }
        }
      }
    },

    mounted: function () {
      let chart     = this.$refs.chart;
      let container = this.$refs.container;
      let tooltip   = d3.select (this.$refs.tooltip);

      let margin = this.margin;

      let barHeight  = this.barHeight;
      let barPadding = 10;

      let groupedData = arrayGroup (this.data, Object.keys (this.series));

      let sortBy = this.sort;
      let series = this.series;

      if (sortBy) {
        groupedData.sort ((j, k) => {
          let d0 = maxValue (j.y);
          let d1 = maxValue (k.y);

          if (sortBy in j.y) {
            d0 = j.y [sortBy];
          }

          if (sortBy in k.y) {
            d1 = k.y [sortBy];
          }

          return d3.ascending (d0, d1);
        });
      }

      for (let group of groupedData) {
        group.total = Object
          .values (group.y)
          .reduce ((x, y) => x + y, 0);
      }

      let boundingBox = chart.getBoundingClientRect ();

      let width = boundingBox.width - margin.left - margin.right;
      // let height = boundingBox.height - margin.top - margin.bottom;

      let height =
          barHeight  * groupedData.length
        + barPadding * groupedData.length;

      let svg = d3
        .select (chart)
        .attr ('width',  width + margin.left + margin.right)
        .attr ('height', height + margin.top + margin.bottom)
        .append ('g')
        .attr ('transform', `translate(${margin.left}, ${margin.top})`);

      let xMin = d3.min (groupedData, d => d3.min (Object.values (d.y)));
      let xMax = d3.max (groupedData, d => d3.max (Object.values (d.y)));

      if (this.stacked) {
        // xMax is actually the maximum sum.
        xMax = d3.max (groupedData, d => d.total);
      }

      let y = d3
        .scaleBand ()
        .range ([ height, 0 ])
        .padding (0.25);

      let y1 = d3
        .scaleBand ()
        .domain (Object.keys (this.data).map (capitalize))
        .rangeRound ([ 0, y.bandwidth () ]);

      let x = d3
        .scaleLinear ()
        .range ([ 0, width ]);

      x
        .domain ([ 0, xMax ])
        .nice ();

      y.domain (arrayColumn (groupedData, 'x'));

      let interpolate = d3
        .scaleLinear ()
        .domain ([ xMin, xMax ])

        .interpolate (d3.interpolateHcl)

        .range ([
            d3.rgb (colour ('red')),
            d3.rgb (colour ('green'))
        ]);

      let bar = svg
        .append ('g')
        .selectAll ('g')
        .data (groupedData)
        .join ('g');

      bar.attr ('transform', d => `translate(0, ${y (d.x)})`)
        .selectAll ('rect')

        .data (d => {
          let offset = x (0);
          let bar;

          return Object
            .keys (d.y)
            .map ((key, index) => {
              bar = {
                key,
                value: d.y [key],
                offsetX: offset,
                offsetY: 0,
                // offsetY: index * barHeight,
                height: barHeight
                // height: barHeight / Object.keys (d.y).length
              };

              offset += x (d.y [key]);
              return bar;
            })
        })

        .join ('rect')
        .attr ('class', 'Bar')
        .attr ('y', d => d.offsetY)
        .attr ('x', d => d.offsetX)
        .attr ('width', d => x (d.value))
        .attr ('height', d => d.height)

        .attr ('fill', d => {
          if ('colour' in series [d.key]) {
            return colour (series [d.key].colour || 'gold');
          }

          return interpolate (+d.value);
        })

        .attr ('opacity', 0.7)

        .on ('mouseover', showTooltip)
        .on ('mouseout', hideTooltip);

      if (this.stacked) {
        bar
          .append ('text')
          .attr ('dy', '.75em')
          .attr ('y', (d, i) => barHeight / 2 - barPadding / 2)
          .attr ('x', d => x (d.total) + 5)
          .attr ('fill', 'rgba(var(--white), 1')
          .text (d => d.total);
      }

      svg
        .append ('g')
        .attr ('transform', `translate(0, ${height})`)
        .call (d3.axisBottom (x));

      svg
        .append ('g')
        .call (d3.axisLeft (y));

      function showTooltip (d) {
        let parent = container.getBoundingClientRect ();
        let child  = this.getBoundingClientRect ();

        let offsetX = child.x - parent.x;
        let offsetY = child.y - parent.y;

        let parentData = d3
          .select (this.parentNode)
          .datum ();

        tooltip
          .html (
            `
              <strong>${capitalize (d.key)}:</strong> ${d.value} <br>
              <strong>Percent:</strong> ${round (d.value / parentData.total * 100)}%
            `)
          .style ('display', 'block');

        let boundingBox = tooltip
          .node ()
          .getBoundingClientRect ();

        let left,
            top;

        left  = offsetX;
        left += this.getAttribute ('width') / 2;
        left -= boundingBox.width / 2;
        // left += margin.left;

        top  = offsetY;
        top -= boundingBox.height;
        top -= 5;

        tooltip
          .style ('left', `${left}px`)
          .style ('top', `${top}px`)
      }

      function hideTooltip (d) {
        tooltip
          .style ('display', 'none');
      }
    }
  };
</script>

<style lang="scss">
  .BarChart {
    position: relative;

    svg {
      height: 100%;
      width: 100%;
    }

    text {
      font-size: fetch(font s);
    }

    .Bar {
      transition: all 0.15s;
    }

    .Bar:hover {
      fill: rgba(var(--white), 0.4);
    }
  }
</style>